/** Tipos da API /beneficios. Valores em centavos, como a folha. */

/** Benefício cadastrado pelo RH em Configurações (GET /beneficios/catalogo). */
export interface BeneficioCatalogo {
  /** Id do benefício cadastrado. */
  tipo: string;
  /** Nome que a empresa usa. */
  nome: string;
  /** Coluna da planilha da Flash em que ele sai (chave, ex. "ALIMENTACAO"). */
  colunaFlash: string;
  colunaNome: string;
  /** Para quais vínculos vale. */
  pj: boolean;
  clt: boolean;
  /** Fixo de cada vínculo; 0 = não é fixo. */
  fixoPj: number;
  fixoClt: number;
  /** Falso = excluído, mas com lançamento em folha antiga. */
  ativo: boolean;
  /** "complemento_salario": valor vem do cadastro do PJ. */
  sistema: string | null;
  ordem: number;
}

/** Coluna da planilha da Flash (GET /beneficios/colunas-flash). */
export interface ColunaFlash {
  tipo: string;
  nome: string;
}

export type VinculoBeneficio = "pj" | "clt";

export const fixoDoVinculo = (b: BeneficioCatalogo, vinculo: string) =>
  vinculo === "clt" ? b.fixoClt : b.fixoPj;

export const valeParaVinculo = (b: BeneficioCatalogo, vinculo: string | null | undefined) =>
  vinculo === "clt" ? b.clt : b.pj;

/** Benefícios ativos que um colaborador do vínculo pode receber. */
export const beneficiosDoVinculo = (
  catalogo: BeneficioCatalogo[],
  vinculo: string | null | undefined,
  { incluirSistema = true }: { incluirSistema?: boolean } = {},
) => catalogo.filter((b) => b.ativo && valeParaVinculo(b, vinculo) && (incluirSistema || !b.sistema));

/** GET /beneficios/colaboradores/:id */
export interface BeneficiosDoColaborador {
  colaborador: { id: string; name: string; vinculo: VinculoBeneficio };
  /** Valores próprios definidos (inclusive 0); tipo ausente segue o fixo. */
  proprios: Record<string, number>;
  /** Fixos do vínculo dele. */
  fixos: Record<string, number>;
  /** Centavos — PJ recebe em toda folha de benefícios (vem do cadastro). */
  complementoSalario: number;
}

export interface FolhaBeneficioResumo {
  id: string;
  /** AAAA-MM — mês a que o benefício se refere. */
  competencia: string;
  /** AAAA-MM-DD — data do pagamento (pode cair no mês anterior à competência). */
  data: string;
  descricao: string | null;
  exportadaEm: string | null;
  createdAt: string;
  totalColaboradores: number;
  total: number;
}

export interface ColaboradorBeneficio {
  id: string;
  name: string;
  cpf: string | null;
  vinculo: string;
  ativo: boolean;
  empresaCnpj: string | null;
  empresaNome: string | null;
}

export interface LinhaFolhaBeneficio {
  colaborador: ColaboradorBeneficio;
  valores: Record<string, number>;
  total: number;
}

/** GET /beneficios/folhas/:id sem `page` (ou com `colaboradorId`, só a linha dele). */
export interface FolhaBeneficioDetalhe extends Omit<FolhaBeneficioResumo, "totalColaboradores"> {
  linhas: LinhaFolhaBeneficio[];
}

export interface EmpresaDaFolha {
  /** Só dígitos. */
  cnpj: string;
  nome: string;
  colaboradores: number;
}

/** Totais da folha inteira — não mudam com a página nem com a busca. */
export interface ResumoFolhaBeneficio {
  total: number;
  totalColaboradores: number;
  /** Centavos por tipo; só os tipos lançados na folha. */
  totaisPorTipo: Record<string, number>;
  /** Sem CPF ou sem tomador (CNPJ) — a Flash recusa. */
  pendenciasCadastro: { total: number; colaboradores: { id: string; name: string }[] };
  empresas: EmpresaDaFolha[];
}

/** GET /beneficios/folhas/:id?page=&pageSize=&q= — `items`/`total` são as linhas (filtradas por `q`). */
export interface PaginaFolhaBeneficio {
  items: LinhaFolhaBeneficio[];
  total: number;
  page: number;
  pageSize: number;
  folha: Omit<FolhaBeneficioResumo, "totalColaboradores" | "total">;
  resumo: ResumoFolhaBeneficio;
}

/** "2026-10-05" -> "05/10/2026", sem passar por Date (evita virar o dia no fuso). */
export const formatDataCivil = (aaaammdd: string) => {
  const [a, m, d] = aaaammdd.split("-");
  return a && m && d ? `${d}/${m}/${a}` : aaaammdd;
};

export const soDigitos = (s: string | null | undefined) => (s ?? "").replace(/\D/g, "");
